import React from 'react';
import { FaTimes, FaCheck, FaUser, FaLock, FaMapMarkerAlt, FaGlobe, FaVenusMars } from 'react-icons/fa';
import styled from 'styled-components';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(11, 19, 32, 0.85);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const Modal = styled.div`
  background: var(--panel-bg, #0f172a);
  border: 1px solid var(--border-soft, #1e293b);
  border-radius: 12px;
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--border-soft, #1e293b);
  background: var(--header-bg, #0b1320);
  border-radius: 12px 12px 0 0;
`;

const Title = styled.h3`
  margin: 0;
  color: var(--text-primary, #f8fafc);
  font-size: 1.1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary, #94a3b8);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  transition: all 0.2s;
  
  &:hover {
    background: var(--border-soft, #1e293b);
    color: var(--text-primary, #f8fafc);
  }
`;

const Content = styled.div`
  padding: 1rem 1.5rem;
  overflow-y: auto;
  flex: 1;
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const StatCard = styled.div`
  background: var(--header-bg, #0b1320);
  padding: 0.75rem;
  border-radius: 8px;
  border: 1px solid var(--border-soft, #1e293b);
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--accent-primary, #3b82f6);
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: var(--text-secondary, #94a3b8);
  margin-top: 0.25rem;
`;

const ProfileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ProfileCard = styled.div`
  background: var(--header-bg, #0b1320);
  border: 1px solid var(--border-soft, #1e293b);
  border-radius: 8px;
  padding: 1rem;
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 1rem;
  align-items: start;
`;

const ProfileIndex = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--accent-primary, #3b82f6);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 600;
  flex-shrink: 0;
`;

const ProfileInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ProfileHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const Username = styled.span`
  font-weight: 600;
  color: var(--text-primary, #f8fafc);
  font-family: monospace;
  font-size: 0.95rem;
`;

const Platform = styled.span`
  background: var(--accent-primary, #3b82f6);
  color: white;
  padding: 0.15rem 0.5rem;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 500;
  text-transform: uppercase;
`;

const DisplayName = styled.span`
  color: var(--text-secondary, #94a3b8);
  font-size: 0.85rem;
`;

const ProfileDetails = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  font-size: 0.8rem;
  color: var(--text-secondary, #94a3b8);
`;

const Detail = styled.span`
  display: flex;
  align-items: center;
  gap: 0.35rem;
`;

const PasswordPreview = styled.code`
  background: var(--panel-bg, #0f172a);
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
  font-family: monospace;
  font-size: 0.75rem;
  color: var(--accent-secondary, #10b981);
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--border-soft, #1e293b);
  background: var(--header-bg, #0b1320);
  border-radius: 0 0 12px 12px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const Button = styled.button`
  padding: 0.6rem 1.25rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  ${props => props.$primary ? `
    background: var(--accent-primary, #3b82f6);
    color: white;
    border: none;
    
    &:hover:not(:disabled) {
      background: #2563eb;
    }
    
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  ` : `
    background: transparent;
    color: var(--text-secondary, #94a3b8);
    border: 1px solid var(--border-soft, #1e293b);
    
    &:hover {
      background: var(--border-soft, #1e293b);
      color: var(--text-primary, #f8fafc);
    }
  `}
`;

const GenderIcon = styled.span`
  font-size: 0.9rem;
`;

const PreviewModal = ({ 
  isOpen, 
  onClose, 
  profiles = [], 
  onConfirm, 
  onCancel,
  isLoading = false,
  platform = 'x'
}) => {
  if (!isOpen || profiles.length === 0) return null;

  const getGenderIcon = (gender) => {
    switch (gender) {
      case 'M': return '♂️';
      case 'F': return '♀️';
      case 'U': return '⚧️';
      default: return '👤';
    }
  };

  const getGenderLabel = (gender) => {
    switch (gender) {
      case 'M': return 'Masculino';
      case 'F': return 'Femenino';
      case 'U': return 'Unisex';
      default: return 'Aleatorio';
    }
  };

  // Calcular estadísticas
  const stats = {
    total: profiles.length,
    male: profiles.filter(p => p._meta?.gender === 'M').length,
    female: profiles.filter(p => p._meta?.gender === 'F').length,
    unisex: profiles.filter(p => p._meta?.gender === 'U').length,
    uniqueCountries: new Set(profiles.map(p => p._meta?.pais).filter(Boolean)).size,
  };

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={e => e.stopPropagation()}>
        <Header>
          <Title>
            <FaUser /> Previsualizar Perfiles Generados
          </Title>
          <CloseButton onClick={onClose}>
            <FaTimes />
          </CloseButton>
        </Header>
        
        <Content>
          <Stats>
            <StatCard>
              <StatValue>{stats.total}</StatValue>
              <StatLabel>Total Perfiles</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>{stats.male}</StatValue>
              <StatLabel>Masculino</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>{stats.female}</StatValue>
              <StatLabel>Femenino</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>{stats.unisex}</StatValue>
              <StatLabel>Unisex</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>{stats.uniqueCountries}</StatValue>
              <StatLabel>Países</StatLabel>
            </StatCard>
          </Stats>

          <ProfileList>
            {profiles.map((profile, idx) => (
              <ProfileCard key={idx}>
                <ProfileIndex>{idx + 1}</ProfileIndex>
                <ProfileInfo>
                  <ProfileHeader>
                    <Username>@{profile.username}</Username>
                    <Platform>{profile.platform || platform}</Platform>
                    <DisplayName>{profile.display_name}</DisplayName>
                  </ProfileHeader>
                  <ProfileDetails>
                    <Detail>
                      <GenderIcon>{getGenderIcon(profile._meta?.gender)}</GenderIcon>
                      {getGenderLabel(profile._meta?.gender)}
                    </Detail>
                    <Detail>
                      <FaMapMarkerAlt />
                      {profile._meta?.ciudad}, {profile._meta?.pais}
                    </Detail>
                    <Detail>
                      <FaGlobe />
                      {profile.zona_horaria?.split('/').pop()}
                    </Detail>
                    <Detail>
                      <FaLock />
                      <PasswordPreview>{profile.password?.slice(0, 12)}...</PasswordPreview>
                    </Detail>
                  </ProfileDetails>
                </ProfileInfo>
              </ProfileCard>
            ))}
          </ProfileList>
        </Content>

        <Footer>
          <span style={{ color: 'var(--text-secondary, #94a3b8)', fontSize: '0.875rem' }}>
            Revisa los perfiles antes de guardarlos
          </span>
          <ButtonGroup>
            <Button onClick={onCancel || onClose}>
              <FaTimes /> Cancelar
            </Button>
            <Button $primary onClick={onConfirm} disabled={isLoading}>
              {isLoading ? (
                <>Generando...</>
              ) : (
                <><FaCheck /> Confirmar y Crear {profiles.length} Perfiles</>
              )}
            </Button>
          </ButtonGroup>
        </Footer>
      </Modal>
    </Overlay>
  );
};

export default PreviewModal;
